import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPinIcon, FunnelIcon, XMarkIcon, ChevronDownIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import NoImg from '../assets/images/no-image-icon-23485.png';
import Navbar from './Navbar';
import Footer from './Footer';
import starGreen from "../assets/icons/StarGreen.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;


interface Place {
  id: string;
  name: string;
  Rate: number;
  ImgName: string;
  address?: string;
  opening_hours: { all_day?: string | null };
  description: string;
  category: string;
  image_urls?: string[];
  review_count: number;
  city?: string;
}

interface Filters {
  city: string;
  minRating: string;
  priceRange: string;
}

const CITIES = [
  { id: 'rasht', name: 'rasht', faName: 'رشت' },
  { id: 'bandar-e-anzali', name: 'bandar-e-anzali', faName: 'بندر انزلی' },
  { id: 'lahijan', name: 'lahijan', faName: 'لاهیجان' },
  { id: 'fuman', name: 'fuman', faName: 'فومن' },
  { id: 'shaft', name: 'shaft', faName: 'شفت' },
  { id: 'khomaam', name: 'khomaam', faName: 'خمام' },
  { id: 'rudsar', name: 'rudsar', faName: 'رودسر' },
  { id: 'masuleh', name: 'masuleh', faName: 'ماسوله' },
  { id: 'talesh', name: 'talesh', faName: 'تالش' },
];


const fetchImageFileUrls = async (placeId: string): Promise<string[]> => {
  try {
    const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
    if (!res.ok) throw new Error("Failed to fetch images");
    const data = await res.json();
    return (data.data.images || []).map((img: any) => img.file_url);
  } catch {
    return [];
  }
};

// تابع جستجو در همان دسته‌بندی
const fetchPlacesByCategoryAndSearch = async (
  category: string,
  page: number,
  pageSize: number,
  filters: Filters,
  searchQuery: string
) => {
  let url = `${BASE_URL}/api/places/?category=${encodeURIComponent(category)}&page=${page}&page_size=${pageSize}`;

  if (filters.city && filters.city.trim() !== '') {
    url += `&city=${encodeURIComponent(filters.city)}`;
  }
  if (filters.minRating && filters.minRating !== '') {
    url += `&min_rating=${filters.minRating}`;
  }
  if (filters.priceRange && filters.priceRange !== '') {
    url += `&price_range=${encodeURIComponent(filters.priceRange)}`;
  }
  if (searchQuery && searchQuery.trim() !== '') {
    url += `&search=${encodeURIComponent(searchQuery)}`;
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`خطا در دریافت ${category}`);

  const json = await res.json();
  const dataArray = json.data || [];

  const formatted: Place[] = await Promise.all(
    dataArray.map(async (item: any) => {
      const imageUrls = await fetchImageFileUrls(item.place_id);
      const categoryName = item.categories?.length > 0 ? item.categories[0].name : category;
      return {
        id: item.place_id,
        name: item.name,
        Rate: item.avg_rating || item.rate || 0,
        ImgName: item.name,
        address: item.address,
        opening_hours: item.opening_hours || { all_day: null },
        description: item.description || '',
        category: categoryName,
        image_urls: imageUrls,
        review_count: item.review_count || 0,
        city: item.city,
      };
    })
  );

  return {
    places: formatted,
    totalPages: json.pagination ? Math.ceil(json.pagination.count / pageSize) : 1,
  };
};

const getImageUrl = (fileUrl?: string) => fileUrl ? `${BASE_URL}${fileUrl}` : NoImg;

// کامپوننت کارت مکان - نسخه دسکتاپ
const DesktopPlaceCard: React.FC<{ place: Place }> = ({ place }) => {
  const image = place.image_urls?.[0];
  const showRating = place.Rate > 0;
  const ratingValue = place.Rate % 1 === 0 ? place.Rate.toFixed(0) : place.Rate.toFixed(1);

  return (
    <Link to={`/places/${place.id}`} className="block group">
      <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300 flex p-3 gap-3">
        <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={getImageUrl(image)}
            alt={place.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>

        <div className="flex flex-col justify-between flex-1">
          <div className="flex flex-col justify-between items-start gap-2 mb-2">
            <h3 className="text-sm font-myYekanDemibold text-gray-800 flex-1" dir="auto">
              {place.name}
            </h3>
            {showRating && (
              <div className="flex gap-1 text-xs text-[#1BA75E] font-myYekanFaNumDemiBold">
                <span>{ratingValue}</span>
                <img src={starGreen} className="w-3 h-3" alt="rating" />
              </div>
            )}
          </div>

          {place.address && (
            <div className="flex items-center text-xs text-gray-500" dir="auto">
              <MapPinIcon className="h-3 w-3 ml-1 text-gray-400 flex-shrink-0" />
              <span className="line-clamp-1 font-myYekanFaNumMedium">{place.address}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// کامپوننت کارت مکان - نسخه موبایل و تبلت
const MobilePlaceCard: React.FC<{ place: Place; isLast: boolean }> = ({ place, isLast }) => {
  const image = place.image_urls?.[0];
  const showRating = place.Rate > 0;
  const ratingValue = place.Rate % 1 === 0 ? place.Rate.toFixed(0) : place.Rate.toFixed(1);

  return (
    <Link to={`/places/${place.id}`} className="block">
      <div className={`py-4 ${!isLast ? 'border-b border-gray-200' : ''}`}>
        <div className="flex gap-3">
          <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
            <img
              src={getImageUrl(image)}
              alt={place.name}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-col justify-between flex-1">
            <div className="flex flex-col justify-between items-start gap-2 mb-1">
              <h2 className="text-sm font-myYekanDemibold text-gray-800 line-clamp-1 flex-1" dir="auto">
                {place.name}
              </h2>
              {showRating && (
                <div className="flex gap-1 text-xs text-[#1BA75E] font-myYekanFaNumMedium flex-shrink-0">
                  <span>{ratingValue}</span>
                  <img src={starGreen} className="w-3 h-3" alt="rating" />
                </div>
              )}
            </div>

            {place.address && (
              <div className="flex items-center text-xs text-gray-500" dir="auto">
                <MapPinIcon className="h-3 w-3 ml-1 text-gray-400 flex-shrink-0" />
                <span className="line-clamp-1 font-myYekanFaNumMedium">{place.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

// کامپوننت مودال انتخاب شهر با اندازه ثابت
const CitySelectionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  selectedCity: string;
  onSelectCity: (city: string) => void;
  onApply: () => void;
}> = ({ isOpen, onClose, selectedCity, onSelectCity, onApply }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedCity, setTempSelectedCity] = useState<string>(selectedCity);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedCity(selectedCity);
      setSearchTerm('');
    }
  }, [isOpen, selectedCity]);

  const filteredCities = CITIES.filter(
    city =>
      city.faName.includes(searchTerm) ||
      city.name.includes(searchTerm)
  );

  const handleSelectCity = (cityName: string) => {
    setTempSelectedCity(cityName);
  };

  const handleRemoveAll = () => {
    setTempSelectedCity('');
  };

  const handleApply = () => {
    onSelectCity(tempSelectedCity);
    onApply();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Desktop Modal */}
      {isDesktop ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-myYekanRegular">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-[500px] h-[500px] flex flex-col overflow-hidden">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-myYekanDemibold text-gray-800">انتخاب شهر</h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="جستجوی شهر..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-4">
              <div className="space-y-2 pb-2">
                <button
                  onClick={() => handleSelectCity('')}
                  className={`w-full text-right px-4 py-3 rounded-lg transition flex items-center justify-between ${tempSelectedCity === ''
                      ? 'bg-green-50 border border-green-200'
                      : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="text-gray-700">همه شهرها</span>
                  {tempSelectedCity === '' && (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                  )}
                </button>

                <div className="border-t border-gray-100 my-2"></div>

                {filteredCities.map((city) => (
                  <button
                    key={city.id}
                    onClick={() => handleSelectCity(city.faName)}
                    className={`w-full text-right px-4 py-3 rounded-lg transition flex items-center justify-between ${tempSelectedCity === city.faName
                        ? 'bg-green-50 border border-green-200'
                        : 'hover:bg-gray-50'
                      }`}
                  >
                    <span className="text-gray-700">{city.faName}</span>
                    {tempSelectedCity === city.faName && (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    )}
                  </button>
                ))}

                {filteredCities.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    شهری یافت نشد
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3 flex-shrink-0">
              <button
                onClick={handleRemoveAll}
                className="flex-1 py-3 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-200 border border-gray-200"
              >
                حذف همه
              </button>

              <button
                onClick={handleApply}
                className="flex-1 py-3 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200"
              >
                مشاهده نتیجه
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Mobile Bottom Sheet */
        <div
          className={`fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
        >
          <div className="bg-white rounded-t-2xl shadow-xl h-[600px] max-h-[80vh] overflow-hidden flex flex-col font-myYekanRegular">              <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg font-myYekanDemibold text-gray-800">انتخاب شهر</h2>

              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="جستجوی شهر..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 px-4">
              <div className="space-y-2 pb-2">
                <button
                  onClick={() => handleSelectCity('')}
                  className={`w-full text-right px-4 py-3 rounded-lg transition flex items-center justify-between ${tempSelectedCity === ''
                      ? 'bg-green-50 border border-green-200'
                      : 'hover:bg-gray-50'
                    }`}
                >
                  <span className="text-gray-700">همه شهرها</span>
                  {tempSelectedCity === '' && (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                  )}
                </button>

                <div className="border-t border-gray-100 my-2"></div>

                {filteredCities.map((city) => (
                  <button
                    key={city.id}
                    onClick={() => handleSelectCity(city.faName)}
                    className={`w-full text-right px-4 py-3 rounded-lg transition flex items-center justify-between ${tempSelectedCity === city.faName
                        ? 'bg-green-50 border border-green-200'
                        : 'hover:bg-gray-50'
                      }`}
                  >
                    <span className="text-gray-700">{city.faName}</span>
                    {tempSelectedCity === city.faName && (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    )}
                  </button>
                ))}

                {filteredCities.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    شهری یافت نشد
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 flex gap-3 flex-shrink-0">
              <button
                onClick={handleRemoveAll}
                className="flex-1 py-3 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-200 border border-gray-200"
              >
                حذف همه
              </button>

              <button
                onClick={handleApply}
                className="flex-1 py-3 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200"
              >
                مشاهده نتیجه
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// کامپوننت فیلد انتخاب شهر (دسکتاپ)
const CitySelectField: React.FC<{
  selectedCity: string;
  onOpenModal: () => void;
}> = ({ selectedCity, onOpenModal }) => {
  const getDisplayText = () => {
    if (!selectedCity) return 'همه شهرها';
    return selectedCity;
  };

  return (
    <div>
      <label className="block text-sm font-myYekanRegular text-gray-700 mb-2">شهر</label>
      <button
        onClick={onOpenModal}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white text-right flex items-center justify-between"
      >
        <span className={selectedCity ? 'text-gray-800 font-myYekanRegular' : 'text-gray-400'}>
          {getDisplayText()}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  );
};

// کامپوننت سرچ بار - نسخه دسکتاپ (در سایدبار)
const DesktopSearchBar: React.FC<{
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  placeholder?: string;
}> = ({ searchValue, onSearchChange, onSearch, placeholder = "جستجو در این دسته..." }) => {

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div>
      <label className="block text-sm font-myYekanRegular text-gray-700 mb-2">جستجو</label>
      <div className="relative">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
        />
        <button
          onClick={onSearch}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
        >
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

// کامپوننت سرچ بار - نسخه موبایل و تبلت (کنار دکمه فیلتر)
const MobileSearchBar: React.FC<{
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  placeholder?: string;
}> = ({ searchValue, onSearchChange, onSearch, placeholder = "جستجو..." }) => {

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div className="relative flex-1">
      <input
        type="text"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
      />
      <button
        onClick={onSearch}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
      >
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
      </button>
    </div>
  );
};

// کامپوننت سایدبار فیلتر (نسخه دسکتاپ) - با سرچ بار
const FilterSidebar: React.FC<{
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  onReset: () => void;
  onOpenCityModal: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
}> = ({
  filters,
  setFilters,
  onReset,
  onOpenCityModal,
  searchValue,
  onSearchChange,
  onSearch
}) => {

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFilters(prev => ({ ...prev, [name]: value }));
    };

    return (
      <div className="bg-white border border-gray-100 rounded-lg p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)] h-fit lg:sticky lg:top-24">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
              <FunnelIcon className="h-4 w-4 text-gray-700" />
            </div>

            <div>
              <h3 className="text-sm font-myYekanDemibold text-gray-800">
                فیلترها
              </h3>
            </div>
          </div>

          <button
            onClick={onReset}
            className="text-xs text-gray-400 font-myYekanRegular hover:text-red-500 transition"
          >
            پاک کردن
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-2 font-myYekanRegular">
            جستجو
          </label>

          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
              placeholder="جستجو در این دسته..."
              className="w-full h-11 rounded-lg border border-gray-200 bg-gray-50 pr-8 pl-3 text-sm focus:outline-none focus:border-green-500 focus:bg-white transition font-myYekanRegular"
            />

            <button
              onClick={onSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* City */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-2 font-myYekanRegular">
            شهر
          </label>

          <button
            onClick={onOpenCityModal}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white transition flex items-center justify-between"
          >
            <span className={`text-sm ${filters.city ? 'text-gray-800' : 'text-gray-400'} font-myYekanRegular`}>
              {filters.city || 'همه شهرها'}
            </span>

            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Rating */}
        <div>
          <label className="block text-xs text-gray-500 mb-2 font-myYekanRegular">
            حداقل امتیاز
          </label>

          <select
            name="minRating"
            value={filters.minRating}
            onChange={handleChange}
            className="w-full h-11 px-4 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-green-500 focus:bg-white transition font-myYekanRegular appearance-none"
          >
            <option value="">همه امتیازات</option>
            <option value="3">۳ ستاره و بالاتر</option>
            <option value="4">۴ ستاره و بالاتر</option>
            <option value="4.5">۴.۵ ستاره و بالاتر</option>
          </select>
        </div>
      </div>
    );
  };

// کامپوننت Bottom Sheet برای موبایل و تبلت
const FilterBottomSheet: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  onReset: () => void;
  onApply: () => void;
  onOpenCityModal: () => void;
}> = ({ isOpen, onClose, filters, setFilters, onReset, onApply, onOpenCityModal }) => {

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getCityDisplayText = () => {
    if (!filters.city) return 'همه شهرها';
    return filters.city;
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 font-myYekanRegular"
        onClick={onClose}
      />

      <div className={`fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="bg-white rounded-t-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

          <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center flex-shrink-0">
            <h2 className="text-lg font-myYekanFaNumRegular text-gray-800">فیلترها</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition">
              <XMarkIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto font-myYekanRegular">
            <div className="p-4 space-y-6">
              <div>
                <label className="block text-sm text-gray-700 mb-2">شهر</label>
                <button
                  onClick={() => {
                    onClose();
                    onOpenCityModal();
                  }}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white text-right flex items-center justify-between"
                >
                  <span className={filters.city ? 'text-gray-800' : 'text-gray-400'}>
                    {getCityDisplayText()}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداقل امتیاز</label>
                <select
                  name="minRating"
                  value={filters.minRating}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
                >
                  <option value="">همه امتیازات</option>
                  <option value="3">۳ ستاره و بالاتر</option>
                  <option value="4">۴ ستاره و بالاتر</option>
                  <option value="4.5">۴.۵ ستاره و بالاتر</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 p-4 flex gap-3 flex-shrink-0 font-myYekanRegular">
            <button
              onClick={() => {
                onReset();
                onApply();
              }}
              className="flex-1 py-3 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-200 border border-gray-200"
            >
              پاک کردن همه
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="flex-1 py-3 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200"
            >
              اعمال فیلترها
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// کامپوننت مودال فیلتر برای دسکتاپ
const FilterModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  onReset: () => void;
  onApply: () => void;
  onOpenCityModal: () => void;
}> = ({ isOpen, onClose, filters, setFilters, onReset, onApply, onOpenCityModal }) => {

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getCityDisplayText = () => {
    if (!filters.city) return 'همه شهرها';
    return filters.city;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
          <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center flex-shrink-0">
            <h2 className="text-lg font-myYekanDemibold text-gray-800">فیلترها</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition">
              <XMarkIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>

          <div className="p-4 space-y-6 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">شهر</label>
              <button
                onClick={() => {
                  onClose();
                  onOpenCityModal();
                }}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white text-right flex items-center justify-between"
              >
                <span className={filters.city ? 'text-gray-800' : 'text-gray-400'}>
                  {getCityDisplayText()}
                </span>
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">حداقل امتیاز</label>
              <select
                name="minRating"
                value={filters.minRating}
                onChange={handleChange}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
              >
                <option value="">همه امتیازات</option>
                <option value="3">۳ ستاره و بالاتر</option>
                <option value="4">۴ ستاره و بالاتر</option>
                <option value="4.5">۴.۵ ستاره و بالاتر</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 p-4 flex gap-3 flex-shrink-0">
            <button
              onClick={() => {
                onReset();
                onApply();
              }}
              className="flex-1 py-3 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-200 border border-gray-200"
            >
              پاک کردن همه
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="flex-1 py-3 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200"
            >
              اعمال فیلترها
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// کامپوننت اسکلت برای حالت دسکتاپ
const DesktopPlaceCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg animate-pulse p-3">
      <div className="flex gap-3">
        <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
};

// کامپوننت اسکلت برای حالت موبایل
const HorizontalPlaceCardSkeleton: React.FC = () => {
  return (
    <div className="py-4 border-b border-gray-200 animate-pulse">
      <div className="flex gap-3">
        <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
};

// کامپوننت صفحه‌بندی
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {

  const getPageNumbers = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible && start > 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center items-center gap-2 mt-8 pt-6 border-t border-gray-200">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`
          p-2 rounded-lg transition duration-200
          ${currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }
        `}
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>

      {getPageNumbers().map(page => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`
            min-w-[40px] h-10 px-3 rounded-lg font-myYekanFaNumRegular transition duration-200
            ${currentPage === page
              ? 'bg-green-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
            }
          `}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`
          p-2 rounded-lg transition duration-200
          ${currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }
        `}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

// کامپوننت اصلی صفحه
const AllPlacesByCategory: React.FC = () => {
const { category } = useParams<{ category: string }>();
const [searchParams] = useSearchParams();                      
const initialCity = searchParams.get('city') ?? '';            
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // State برای سرچ
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

const [filters, setFilters] = useState<Filters>({
  city: initialCity,
  minRating: '',
  priceRange: ''
});

const [tempFilters, setTempFilters] = useState<Filters>({
  city: initialCity,    
  minRating: '',
  priceRange: ''
});

  const pageSize = 15;

  // Debounce برای سرچ
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // ریست صفحه به 1 وقتی فیلترها یا سرچ تغییر می‌کنند
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeFilterCount = (filters.city ? 1 : 0) +
    (filters.minRating !== '' ? 1 : 0) +
    (filters.priceRange !== '' ? 1 : 0);

  useEffect(() => {
    if (category) {
      const savedPage = sessionStorage.getItem(`places_page_${category}`);
      if (savedPage) {
        setCurrentPage(Number(savedPage));
      } else {
        setCurrentPage(1);
      }
    }
  }, [category]);

  useEffect(() => {
    if (category) {
      sessionStorage.setItem(`places_page_${category}`, String(currentPage));
    }
  }, [currentPage, category]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['places', category, currentPage, filters, debouncedSearch],
    queryFn: () => fetchPlacesByCategoryAndSearch(category!, currentPage, pageSize, filters, debouncedSearch),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!category,
  });

  const places = data?.places || [];
  const totalPages = data?.totalPages || 1;

  const handlePageChange = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(page);
  };

  const handleResetFilters = () => {
    setFilters({
      city: '',
      minRating: '',
      priceRange: ''
    });
    setTempFilters({
      city: '',
      minRating: '',
      priceRange: ''
    });
    setSearchValue('');
    setDebouncedSearch('');
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
  };

  const handleSelectCity = (city: string) => {
    setFilters(prev => ({ ...prev, city }));
    setTempFilters(prev => ({ ...prev, city }));
  };

  const handleSearch = () => {
    // جستجو با مقدار فعلی searchValue انجام می‌شود
    // debounce به طور خودکار انجام می‌شود
    setDebouncedSearch(searchValue);
    setCurrentPage(1);
  };

  if (!category) return <div className="text-center py-20 text-red-600">دسته‌بندی مشخص نشده است</div>;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="w-full desktop:px-[200px] px-[16px] py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-myYekanDemibold text-gray-800">
            {category}
          </h1>
          {!isLoading && places.length > 0 && (
            <span className="text-xs text-gray-500 hidden sm:inline font-myYekanFaNumRegular">
              {places.length} مکان
            </span>
          )}
        </div>


{/* Mobile & Tablet Search + Filter */}
<div className="lg:hidden mb-5">
  <div className="flex items-center gap-2">

    {/* Search bar */}
    <div className="flex-1 flex items-center bg-white border border-gray-200 rounded-2xl px-2 py-2 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
      <div className="relative flex-1">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          placeholder={`جستجو در ${category}...`}
          className="w-full bg-transparent pr-9 pl-2 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none font-myYekanRegular"
        />

        <button
          onClick={handleSearch}
          className="absolute right-0 top-1/2 -translate-y-1/2"
        >
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </button>
      </div>
    </div>

    {/* Filter Button - outside search bar */}
    <button
      onClick={handleOpenFilterModal}
      className="relative flex items-center justify-center w-12 h-12 rounded-2xl bg-white border border-gray-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:bg-gray-50 transition flex-shrink-0"
    >
      <FunnelIcon className="h-5 w-5 text-gray-700" />

      {activeFilterCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-green-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-myYekanFaNumRegular">
          {activeFilterCount}
        </span>
      )}
    </button>

  </div>
</div>


        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="hidden lg:block w-full lg:w-1/5">
            <FilterSidebar
              filters={filters}
              setFilters={setFilters}
              onReset={handleResetFilters}
              onOpenCityModal={() => setIsCityModalOpen(true)}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearch={handleSearch}
            />
          </div>

          {/* نمایش Bottom Sheet در موبایل و تبلت */}
          {!isDesktop && (
            <FilterBottomSheet
              isOpen={isFilterModalOpen}
              onClose={() => setIsFilterModalOpen(false)}
              filters={tempFilters}
              setFilters={setTempFilters}
              onReset={() => {
                setTempFilters({
                  city: '',
                  minRating: '',
                  priceRange: ''
                });
              }}
              onApply={handleApplyFilters}
              onOpenCityModal={() => {
                setIsFilterModalOpen(false);
                setIsCityModalOpen(true);
              }}
            />
          )}

          {/* نمایش مودال معمولی در دسکتاپ */}
          {isDesktop && (
            <FilterModal
              isOpen={isFilterModalOpen}
              onClose={() => setIsFilterModalOpen(false)}
              filters={tempFilters}
              setFilters={setTempFilters}
              onReset={() => {
                setTempFilters({
                  city: '',
                  minRating: '',
                  priceRange: ''
                });
              }}
              onApply={handleApplyFilters}
              onOpenCityModal={() => {
                setIsFilterModalOpen(false);
                setIsCityModalOpen(true);
              }}
            />
          )}

          <CitySelectionModal
            isOpen={isCityModalOpen}
            onClose={() => setIsCityModalOpen(false)}
            selectedCity={filters.city}
            onSelectCity={handleSelectCity}
            onApply={() => {
              setCurrentPage(1);
            }}
          />

          <div className="w-full lg:w-4/5">
            {isLoading ? (
              <>
                <div className="hidden lg:grid lg:grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <DesktopPlaceCardSkeleton key={i} />
                  ))}
                </div>
                <div className="lg:hidden space-y-0">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <HorizontalPlaceCardSkeleton key={i} />
                  ))}
                </div>
              </>
            ) : isError ? (
              <div className="text-center py-20 text-red-600 bg-red-50 rounded-lg">
                {(error as Error).message}
              </div>
            ) : (
              <>
                {places.length > 0 ? (
                  <>
                    <div className="hidden lg:grid lg:grid-cols-3 gap-3 ">
                      {places.map((p) => (
                        <DesktopPlaceCard key={p.id} place={p} />
                      ))}
                    </div>

                    <div className="lg:hidden space-y-0">
                      {places.map((p, index) => (
                        <MobilePlaceCard
                          key={p.id}
                          place={p}
                          isLast={index === places.length - 1}
                        />
                      ))}
                    </div>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </>
                ) : (
                  <div className="text-center py-20 text-gray-500 bg-white rounded-lg shadow-sm border font-myYekanFaNumRegular">
                    <p className="text-lg mb-2">موردی یافت نشد</p>

                    <p className="text-sm">
                      لطفاً فیلترها یا عبارت جستجوی خود را تغییر دهید.
                    </p>

                    <button
                      onClick={handleResetFilters}
                      className="mt-4 text-green-600 hover:underline text-sm"
                    >
                      پاک کردن فیلترها
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AllPlacesByCategory;